<template>
  <div class="cbz-metrics">
    <!-- Tarjeta 1: Total por cobrar -->
    <button class="cbz-metric-card" style="border-top-color: #3b82f6" @click="expanded1 = !expanded1">
      <span class="cbz-metric-card__label">{{ accumulated ? 'Total pendiente acumulado' : 'Total por cobrar' }}</span>
      <span class="cbz-metric-card__sublabel">{{ periodLabel }}</span>
      <span class="cbz-metric-card__value">{{ formatCurrency(metrics.totalToCollect.amount) }}</span>

      <span class="cbz-metric-card__sub">{{ metrics.totalToCollect.tenantCount }} de {{ metrics.totalToCollect.activeContracts }} inquilinos con cargos</span>
      <span class="cbz-metric-card__sub cbz-metric-card__sub--muted">
        Renta {{ formatCompact(metrics.totalToCollect.byType.RENT) }} · Servicios {{ formatCompact(metrics.totalToCollect.byType.SERVICE) }} · Adicionales {{ formatCompact(metrics.totalToCollect.byType.OTHER) }}
      </span>
      <span v-if="metrics.usd?.pending > 0" class="cbz-metric-card__sub cbz-metric-card__sub--muted">Aparte, en dólares: {{ formatUsd(metrics.usd.pending) }} por cobrar</span>
      <span v-if="metrics.totalToCollect.hasSignificantDiff" class="cbz-metric-card__sub" :class="metrics.totalToCollect.diff > 0 ? 'cbz-metric-card__amber' : 'cbz-metric-card__green'">
        {{ metrics.totalToCollect.diff > 0 ? '▲' : '▼' }} {{ metrics.totalToCollect.diff > 0 ? '+' : '' }}{{ formatCompact(metrics.totalToCollect.diff) }} vs {{ prevMonthLabel }}
      </span>

      <div v-if="expanded1" class="cbz-metric-card__expand" @click.stop>
        <table class="cbz-metric-card__table">
            <tbody>
                <tr><td>Renta</td><td>{{ formatCurrency(metrics.totalToCollect.byType.RENT) }}</td></tr>
                <tr><td>Servicios</td><td>{{ formatCurrency(metrics.totalToCollect.byType.SERVICE) }}</td></tr>
                <tr><td>Adicionales</td><td>{{ formatCurrency(metrics.totalToCollect.byType.OTHER) }}</td></tr>
            </tbody>
            </table>
      </div>
    </button>

    <!-- Tarjeta 2: Total cobrado -->
    <button class="cbz-metric-card" :style="{ borderTopColor: collectedColor }" @click="$emit('filter-al-corriente')">
      <span class="cbz-metric-card__label">Total cobrado<CollectedInfo :park-id="parkId" :period="period" basis="period" title="¿Cómo se calcula el total cobrado?" :paragraphs="COLLECTED_INFO" /></span>
      <span class="cbz-metric-card__sublabel">efectivo{{ accumulated ? ' · mes actual' : '' }}</span>
      <span class="cbz-metric-card__value" :style="{ color: collectedColor }">{{ formatCurrency(metrics.totalCollected.amount) }}</span>

      <span class="cbz-metric-card__sub" :style="{ color: collectedColor, fontWeight: 500 }">{{ metrics.totalCollected.percent }}% de cobranza</span>
      <div class="cbz-metric-card__progress-bg">
        <div class="cbz-metric-card__progress-fill" :style="{ width: metrics.totalCollected.percent + '%', background: collectedColor }"></div>
      </div>
      <span v-if="metrics.totalCollected.prevPercent !== null" class="cbz-metric-card__sub" :class="metrics.totalCollected.percent >= metrics.totalCollected.prevPercent ? 'cbz-metric-card__green' : 'cbz-metric-card__red'">
        {{ metrics.totalCollected.percent >= metrics.totalCollected.prevPercent ? '▲ +' : '▼ −' }}{{ Math.abs(metrics.totalCollected.percent - metrics.totalCollected.prevPercent).toFixed(1) }}% vs {{ prevMonthLabel }}
      </span>
      <span v-if="metrics.usd?.collected > 0" class="cbz-metric-card__sub cbz-metric-card__sub--muted">Aparte, en dólares: {{ formatUsd(metrics.usd.collected) }} cobrados</span>
      <span class="cbz-metric-card__sub cbz-metric-card__sub--muted">Efectivo recibido en el mes: {{ formatCurrency(metrics.totalCollected.cashReceived) }}</span>
      <span class="cbz-metric-card__sub cbz-metric-card__sub--muted">{{ metrics.totalCollected.paidCompleteCount }} de {{ metrics.totalCollected.totalContracts }} inquilinos pagaron completo</span>
    </button>

    <!-- Tarjeta 3: Cartera vencida -->
    <button class="cbz-metric-card" :style="{ borderTopColor: overdueColor }" @click="$emit('filter-vencidos-by-age')">
      <span v-if="metrics.overdue.buckets.d60plus > 0" class="cbz-metric-card__blink"></span>
      <span class="cbz-metric-card__label">Cartera vencida</span>
      <span class="cbz-metric-card__sublabel">total acumulado{{ accumulated ? '' : ' · ya incluye todos los periodos' }}</span>
      <span class="cbz-metric-card__value" :style="{ color: overdueColor }">{{ formatCurrency(metrics.overdue.total) }}</span>

      <div class="cbz-metric-card__aging">
        <div class="cbz-metric-card__aging-item"><span class="cbz-metric-card__aging-dot" style="background:#f0a500"></span><span class="cbz-metric-card__aging-label">&lt;30d</span><span class="cbz-metric-card__aging-amount" style="color:#f0a500">{{ formatCompact(metrics.overdue.buckets.d30) }}</span></div>
        <div class="cbz-metric-card__aging-item"><span class="cbz-metric-card__aging-dot" style="background:#f97316"></span><span class="cbz-metric-card__aging-label">30-60d</span><span class="cbz-metric-card__aging-amount" style="color:#f97316">{{ formatCompact(metrics.overdue.buckets.d60) }}</span></div>
        <div class="cbz-metric-card__aging-item"><span class="cbz-metric-card__aging-dot" style="background:#e84545"></span><span class="cbz-metric-card__aging-label">&gt;60d</span><span class="cbz-metric-card__aging-amount" style="color:#e84545">{{ formatCompact(metrics.overdue.buckets.d60plus) }}</span></div>
      </div>
      <span v-if="metrics.usd?.overdue > 0" class="cbz-metric-card__sub cbz-metric-card__sub--muted">Aparte, en dólares: {{ formatUsd(metrics.usd.overdue) }}</span>
    </button>

    <!-- Tarjeta 4: Inquilinos vencidos -->
    <button class="cbz-metric-card" :style="{ borderTopColor: overdueTenantsColor }" @click="$emit('filter-vencidos')">
      <span class="cbz-metric-card__label">Inquilinos vencidos</span>
      <span class="cbz-metric-card__sublabel">con saldo sin pagar{{ accumulated ? '' : ' · ya incluye todos los periodos' }}</span>
      <span class="cbz-metric-card__value cbz-metric-card__value--count" :style="{ color: overdueTenantsColor }">{{ metrics.overdueTenants.count }}</span>

      <span class="cbz-metric-card__sub" :style="{ color: overdueTenantsColor, fontWeight: 500 }">{{ metrics.overdueTenants.count }} de {{ metrics.overdueTenants.totalContracts }} inquilinos</span>
      <div class="cbz-metric-card__progress-bg">
        <div class="cbz-metric-card__progress-fill" :style="{ width: overdueTenantsPercent + '%', background: overdueTenantsColor }"></div>
      </div>
      <span class="cbz-metric-card__sub cbz-metric-card__sub--muted">Promedio {{ formatCurrency(metrics.overdueTenants.avgDebt) }} por inquilino</span>
      <span v-if="metrics.overdueTenants.top.length" class="cbz-metric-card__sub cbz-metric-card__sub--muted">
        Mayor: {{ metrics.overdueTenants.top.map(t => `${t.tenantName} ${formatCompact(t.amount)}`).join(' · ') }}
      </span>
    </button>
  </div>
</template>

<script setup>
import CollectedInfo from '@/components/Global/CollectedInfo.vue'
import { ref, computed } from 'vue'

const COLLECTED_INFO = [
  'Es la suma de lo ya abonado a los cargos de este periodo (renta, servicios, mantenimiento y cargos adicionales), según el mes al que pertenece cada cargo.',
  'Un abono cuenta cuando se aplica a un cargo. Si todavía no existe el cargo al que se aplicaría (un adelanto o saldo a favor), no suma hasta que exista.',
  'Los pagos se cuentan en el periodo del cargo, no en la fecha en que se recibió el dinero.',
  'Para ver todo el dinero que entró en el mes (incluidos adelantos y abonos a otros periodos), consulta «Efectivo recibido» al final de este desglose.',
]

const props = defineProps({
  parkId: { type: String, default: '' },
  metrics: { type: Object, required: true },
  period: { type: String, required: true },
  accumulated: { type: Boolean, default: false },
})

defineEmits(['filter-al-corriente', 'filter-vencidos', 'filter-vencidos-by-age'])

const expanded1 = ref(false)

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

const periodLabel = computed(() => {
  const [y, m] = props.period.split('-').map(Number)
  return `${MONTHS[m - 1]} ${y}`
})

const prevMonthLabel = computed(() => {
  const [y, m] = props.period.split('-').map(Number)
  const d = new Date(y, m - 2, 1)
  return MONTHS[d.getMonth()]
})

const collectedColor = computed(() => {
  const p = props.metrics.totalCollected.percent
  if (p >= 90) return '#10b981'
  if (p >= 70) return '#f0a500'
  return '#e84545'
})

const overdueColor = computed(() => {
  const p = props.metrics.overdue.percentOfTotal
  const warning = props.metrics.warningPct ?? 2
  const critical = props.metrics.criticalPct ?? 8
  if (p <= 0) return '#94a3b8'
  if (p <= warning) return '#94a3b8'
  if (p <= critical) return '#f0a500'
  return '#e84545'
})

const overdueTenantsColor = computed(() => {
  const c = props.metrics.overdueTenants.count
  if (c === 0) return '#94a3b8'
  if (c <= 2) return '#f0a500'
  return '#e84545'
})

const overdueTenantsPercent = computed(() => {
  const t = props.metrics.overdueTenants
  return t.totalContracts === 0 ? 0 : Math.round((t.count / t.totalContracts) * 100)
})

function formatUsd(n) {
  return `US$ ${Math.round(n || 0).toLocaleString('es-MX')}`
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0)
}

function formatCompact(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0, notation: 'compact' }).format(n || 0)
}
</script>

<style scoped>
.cbz-metrics {
  display: flex;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  min-height: 96px;
}

.cbz-metric-card {
  flex: 1;
  min-width: 0;
  position: relative;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  border-top: 3px solid transparent;
  padding: 0.55rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.cbz-metric-card__label { font-family: var(--font-body); font-size: 11px; font-weight: 500; color: #64748b; }
.cbz-metric-card__sublabel { font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-top: -0.1rem; }

.cbz-metric-card__value {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 24px;
  color: #1e3a5f;
  line-height: 1.15;
}

.cbz-metric-card__value--count { font-family: var(--font-display); font-size: 28px; }

.cbz-metric-card__sub {
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
  width: 100%;
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cbz-metric-card__sub--muted { color: #94a3b8; font-size: 10px; }
.cbz-metric-card__green { color: #10b981; }
.cbz-metric-card__amber { color: #d97706; }
.cbz-metric-card__red { color: #dc2626; }

.cbz-metric-card__progress-bg { height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden; margin: 0.15rem 0; }
.cbz-metric-card__progress-fill { height: 100%; }

.cbz-metric-card__aging { display: flex; margin-top: 0.2rem; }
.cbz-metric-card__aging-item { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 0.15rem; border-right: 1px solid #f1f5f9; padding: 0 0.2rem; }
.cbz-metric-card__aging-item:last-child { border-right: none; }
.cbz-metric-card__aging-dot { width: 8px; height: 8px; border-radius: 50%; }
.cbz-metric-card__aging-label { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.cbz-metric-card__aging-amount { font-family: var(--font-mono); font-size: 11px; }

.cbz-metric-card__blink {
  position: absolute; top: 8px; right: 8px; width: 6px; height: 6px; border-radius: 50%; background: #e84545;
  animation: cbz-metric-blink 1.4s ease-in-out infinite;
}
@keyframes cbz-metric-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

.cbz-metric-card__expand { margin-top: 0.4rem; border-top: 1px solid #f1f5f9; padding-top: 0.4rem; }
.cbz-metric-card__table { width: 100%; font-family: var(--font-body); font-size: 11px; border-collapse: collapse; }
.cbz-metric-card__table td { padding: 0.2rem 0; color: #1e3a5f; }
.cbz-metric-card__table td:last-child { text-align: right; font-family: var(--font-mono); }
.cbz-metrics {
  display: flex;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  min-height: 96px;
  height: auto;
  container-type: inline-size;
  container-name: cbz-metrics;
}

.cbz-metric-card {
  flex: 1;
  min-width: 0;
  position: relative;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  border-top: 3px solid transparent;
  padding: 0.55rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.cbz-metric-card__value {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 22px;
  color: #1e3a5f;
  line-height: 1.15;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cbz-metric-card__value--count {
  font-family: var(--font-display);
  font-size: 26px;
}

.cbz-metric-card__sub {
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
  width: 100%;
  min-width: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
@container cbz-metrics (max-width: 900px) {
  .cbz-metrics .cbz-metric-card__sub--muted {
    display: none;
  }
  .cbz-metrics .cbz-metric-card__aging-label {
    display: none;
  }
}

@container cbz-metrics (max-width: 650px) {
  .cbz-metrics .cbz-metric-card__value {
    font-size: 18px;
  }
  .cbz-metrics .cbz-metric-card__sub:nth-of-type(n+2) {
    display: none;
  }
}

@container cbz-metrics (max-width: 650px) {
  .cbz-metrics .cbz-metric-card__table {
    font-size: 10px;
  }
}

.cbz-metric-card__expand {
  margin-top: 0.4rem;
  border-top: 1px solid #f1f5f9;
  padding-top: 0.4rem;
  overflow: hidden;
}

.cbz-metric-card__table {
  width: 100%;
  table-layout: fixed;
  font-family: var(--font-body);
  font-size: 11px;
  border-collapse: collapse;
}

.cbz-metric-card__table td {
  padding: 0.2rem 0;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>